import type { ChangeType, ColumnSubType, ReportColumn } from '../../models/column.types'

import { prettifyField } from './normalizeDefinitions'

export type WorkspaceAttributeValue = (key: string) => unknown

type UnknownRecord = Record<string, unknown>

function record(value: unknown): UnknownRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as UnknownRecord)
    : {}
}

function text(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value : null
}

function adjustDescription(field: string, getAttribute: WorkspaceAttributeValue): string | null {
  if (
    !field.startsWith('mmp_') &&
    !field.startsWith('cvr_') &&
    !['network_cost', 'ecpi', 'skad_ecpi'].includes(field)
  )
    return null
  const config = record(getAttribute('adjust_configurations'))
  const type = text(config.attribution_type) ?? 'click'
  const source = text(config.attribution_source) ?? 'dynamic'
  const title = (value: string): string =>
    value.charAt(0).toUpperCase() + value.slice(1).replaceAll('_', ' ')
  return `Attribution Type: ${title(type)}. Attribution Source: ${title(source)}`
}

function nameOf(value: unknown): string | null {
  return text(value) ?? text(record(value)['name'])
}

function indexedValue(value: unknown, field: string): unknown {
  return record(value)[field]
}

function indexOf(field: string): number | null {
  const match = /(\d+)$/u.exec(field)
  return match?.[1] ? Number(match[1]) : null
}

function keyEventLabel(
  field: string,
  source: 'ga4' | 'mmp' | 'hardal',
  value: unknown,
): string | null {
  const suffix = field.endsWith('_d0')
    ? ' D0'
    : field.endsWith('_w0')
      ? ' W0'
      : field.endsWith('_m0')
        ? ' M0'
        : ''
  const isValue = source === 'mmp' && field.endsWith('_value')
  const clearField = field.replace(/_(?:d0|w0|m0)$/u, '').replace(isValue ? /_value$/u : /$^/u, '')
  const entry = Object.entries(record(value)).find(([, mappedField]) => mappedField === clearField)
  if (!entry) return null

  const label = entry[0].replace(/^ga4_keyevents:/u, '').replace(/([\p{Ll}\d])(\p{Lu})/gu, '$1 $2')
  return `${prettifyField(label)}${suffix}${isValue ? ' Value' : ''}`
}

function groupingLabel(field: string, getAttribute: WorkspaceAttributeValue): string | null {
  const custom = getAttribute('custom_grouping')
  if (Array.isArray(custom)) {
    const match = custom.find((item) => record(item)['slot_name'] === field)
    const label = nameOf(match)
    if (label) return label
  }

  const index = indexOf(field)
  if (index === null) return null
  if (field.startsWith('custom_group_name'))
    return text(
      indexedValue(getAttribute('master_campaign_groups'), `custom_master_group_name_${index}`),
    )
  if (field.startsWith('adgroup_custom_group_'))
    return text(indexedValue(getAttribute('adgroup_groups'), field))
  if (field.startsWith('product_custom_group_'))
    return text(indexedValue(getAttribute('product_groups'), field))
  return null
}

function labelSlot(field: string, getAttribute: WorkspaceAttributeValue): string | null {
  const entity = field.startsWith('campaign_label_')
    ? 'campaign'
    : field.startsWith('adgroup_label_')
      ? 'adgroup'
      : null
  const index = indexOf(field)
  const labels = getAttribute('custom_labelling')
  if (!entity || index === null || !Array.isArray(labels)) return null
  const match = labels.find((item) => {
    const candidate = record(item)
    return candidate['entity_type'] === entity && Number(candidate['slot']) === index
  })
  return nameOf(match)
}

interface WorkspaceMapping {
  header: string | null
  headerImage?: string
  numeric?: boolean
  sub_type?: ColumnSubType
  change_type?: ChangeType
}

function keyEventMapping(
  field: string,
  getAttribute: WorkspaceAttributeValue,
): WorkspaceMapping | null {
  if (field.startsWith('key_events_'))
    return {
      header: keyEventLabel(field, 'ga4', getAttribute('ga4_key_event_mapping')),
      headerImage: 'ga4',
      numeric: true,
    }
  if (field.startsWith('hardal_event_'))
    return {
      header: keyEventLabel(field, 'hardal', getAttribute('hardal_events_mapping')),
      headerImage: 'hardal',
      numeric: true,
    }
  if (field.startsWith('mmp_key_events'))
    return {
      header: keyEventLabel(field, 'mmp', getAttribute('mmp_key_event_mapping')),
      headerImage: 'adjust',
      numeric: true,
    }
  return null
}

function configuredMapping(
  field: string,
  getAttribute: WorkspaceAttributeValue,
): WorkspaceMapping | null {
  const group = groupingLabel(field, getAttribute)
  if (group) return { header: group }
  const slotLabel = labelSlot(field, getAttribute)
  if (slotLabel) return { header: slotLabel }

  const index = indexOf(field)
  if (index === null) return null
  if (field.startsWith('custom_conversion_'))
    return {
      header: nameOf(indexedValue(getAttribute('metaads_custom_conversions_mapping'), field)),
    }
  if (field.startsWith('custom_metric'))
    return {
      header: text(indexedValue(getAttribute('custom_metric_mapping'), `custom_metric_${index}`)),
    }
  if (field.startsWith('ws_custom_metric_')) {
    const metric = indexedValue(getAttribute('workspace_custom_metrics'), field)
    const config = record(metric)
    const format = text(config['format'])
    const direction = config['change_type']
    return {
      header: nameOf(metric),
      numeric: true,
      sub_type: format === 'currency' || format === 'percentage' ? format : 'numeric',
      ...(direction === 'positive' || direction === 'negative'
        ? { change_type: direction }
        : direction === 'neutral' || direction === 'notr'
          ? { change_type: null }
          : {}),
    }
  }
  if (field.startsWith('ws_conditional_metric_'))
    return {
      header: nameOf(indexedValue(getAttribute('workspace_custom_conditional_metrics'), field)),
    }

  const sources: Array<[string, string, string]> = [
    ['fp_custom_dimension_', 'custom_campaign_bq_data', 'bq'],
    ['fp_custom_metric_', 'custom_campaign_bq_data', 'bq'],
    ['sheet_custom_dimension_', 'custom_campaign_sheet_data', 'sheet'],
    ['sheet_custom_metric_', 'custom_campaign_sheet_data', 'sheet'],
    ['fp_adgroup_custom_dimension_', 'custom_adgroup_bq_data', 'bq'],
    ['sheet_adgroup_custom_dimension_', 'custom_adgroup_sheet_data', 'sheet'],
    ['custom_campaign_mail_metric_', 'custom_campaign_mail_mapping', 'mail'],
    ['custom_campaign_mail_dimension_', 'custom_campaign_mail_mapping', 'mail'],
    ['custom_adgroup_mail_metric_', 'custom_adgroup_mail_mapping', 'mail'],
    ['custom_adgroup_mail_dimension_', 'custom_adgroup_mail_mapping', 'mail'],
    ['fp_custom_product_metric_', 'custom_product_bq_data', 'bq'],
    ['fp_custom_customer_metadata_', 'custom_customer_bq_data', 'bq'],
    ['fp_custom_customer_metric_', 'custom_customer_bq_data', 'bq'],
    ['fp_custom_order_metadata_', 'custom_order_bq_data', 'bq'],
    ['fp_custom_order_metric_', 'custom_order_bq_data', 'bq'],
    ['fp_hotel_custom_dimension', 'custom_hotel_bq_data', 'google'],
  ]
  const source = sources.find(([prefix]) => field.startsWith(prefix))
  if (!source) return null
  return {
    header: nameOf(indexedValue(getAttribute(source[1]), field)),
    headerImage: source[2],
  }
}

const IMAGE_PREFIX_RULES: readonly (readonly [string, string])[] = [
  ['organic_', 'google_search_console'],
  ['fp_', 'bigquery'],
  ['sheet_', 'google_sheets'],
  ['mp_', 'ga4'],
  ['cvr_', 'adjust'],
  ['calculated_', 'none'],
  ['hardal_', 'hardal'],
  ['iap_', 'adapty'],
]

function prefixImage(field: string): string | null {
  const rule = IMAGE_PREFIX_RULES.find(([prefix]) => field.startsWith(prefix))
  return rule ? rule[1] : null
}

/** Applies workspace-authored names at the catalog read boundary so every column consumer agrees. */
export function mapWorkspaceColumn(
  field: string,
  catalogColumn: ReportColumn | null | undefined,
  getAttribute: WorkspaceAttributeValue,
): ReportColumn | null {
  const mapping = keyEventMapping(field, getAttribute) ?? configuredMapping(field, getAttribute)
  const image = mapping?.headerImage ?? prefixImage(field)
  const description = adjustDescription(field, getAttribute)
  const calculated = field.startsWith('calculated_')
  if (!mapping) {
    if (!catalogColumn) return null
    return {
      ...catalogColumn,
      ...(image ? { headerImage: image } : {}),
      ...(description ? { description } : {}),
      ...(calculated ? { isCalculated: true } : {}),
    }
  }
  if (!catalogColumn && !mapping.header) return null

  return {
    ...(catalogColumn ?? { field, header: prettifyField(field) }),
    ...(mapping.header ? { header: mapping.header } : {}),
    ...(image ? { headerImage: image } : {}),
    ...(description ? { description } : {}),
    ...(mapping.numeric === undefined ? {} : { numeric: mapping.numeric }),
    ...(mapping.sub_type ? { sub_type: mapping.sub_type } : {}),
    ...(mapping.change_type === undefined ? {} : { change_type: mapping.change_type }),
    ...(calculated ? { isCalculated: true } : {}),
  }
}
